// src/features/operational/components/Dashboard.js
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';
import { useAuth } from '../../../contexts/AuthContext';

// StyledCard to ensure consistent modern styling across cards.
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  width: '100%',
  height: '100%',
  boxShadow: theme.shadows[2],
}));

// A reusable component for progress cards.
const ProgressCard = ({ title, icon: IconComponent, progressData, progressColor, iconBg }) => (
  <StyledCard elevation={2}>
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
        <Avatar sx={{ bgcolor: iconBg, width: 35, height: 35 }}>
          <IconComponent sx={{ color: progressColor, fontSize: 20 }} />
        </Avatar>
      </Box>
      <Box mb={1}>
        <LinearProgress
          variant="determinate"
          value={progressData.percentage}
          sx={{
            height: 8,
            borderRadius: 5,
            bgcolor: iconBg,
            '& .MuiLinearProgress-bar': { bgcolor: progressColor }
          }}
        />
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body2" color="textSecondary">
          {progressData.complete}/{progressData.total} complete
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {progressData.percentage}%
        </Typography>
      </Box>
    </CardContent>
  </StyledCard>
);

const Dashboard = () => {
  const { userProfile } = useAuth();

  // Mock dashboard data
  const dashboardData = {
    subjectPlanning: {
      complete: 8,
      total: 10,
      percentage: 80
    },
    assessments: {
      complete: 18,
      total: 30,
      percentage: 60
    },
    lessonPlans: {
      complete: 15,
      total: 20,
      percentage: 75
    },
    moderation: {
      complete: 5,
      total: 8,
      percentage: 62.5
    },
    upcomingTasks: [
      { id: 1, title: 'Submit Term 2 ATP', dueDate: '2025-04-20', status: 'urgent' },
      { id: 2, title: 'Grade 10 Physics Assessment', dueDate: '2025-04-25', status: 'normal' },
      { id: 3, title: 'Learner Progress Reports', dueDate: '2025-05-05', status: 'normal' },
      { id: 4, title: 'CPTD Workshop Registration', dueDate: '2025-04-18', status: 'urgent' }
    ],
    recentActivities: [
      { id: 1, title: 'Updated Grade 11 Chemistry Lesson Plan', date: '2025-04-15', type: 'lesson' },
      { id: 2, title: 'Created Natural Science Assessment Task', date: '2025-04-14', type: 'assessment' },
      { id: 3, title: 'Completed CPTD Self-Assessment', date: '2025-04-12', type: 'personal' },
      { id: 4, title: 'Updated Grade 9 Attendance Register', date: '2025-04-10', type: 'learner' }
    ]
  };

  // Return the appropriate icon based on task status.
  const getStatusIcon = (status) => {
    switch (status) {
      case 'urgent':
        return <WarningIcon sx={{ color: '#f44336' }} />;
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      default:
        return <PendingIcon sx={{ color: '#ff9800' }} />;
    }
  };

  // Return the correct icon based on activity type.
  const getActivityIcon = (type) => {
    switch (type) {
      case 'lesson':
        return <MenuBookIcon sx={{ color: THEME_COLORS.primary }} />;
      case 'assessment':
        return <AssignmentIcon sx={{ color: '#f44336' }} />;
      case 'personal':
        return <SchoolIcon sx={{ color: '#4caf50' }} />;
      case 'learner':
        return <DescriptionIcon sx={{ color: '#ff9800' }} />;
      default:
        return <EventIcon sx={{ color: '#2196f3' }} />;
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Progress Overview Section */}
      <Grid item xs={12} width='100%' alignItems="stretch">
        <Typography variant="h6" fontWeight="medium" mb={2}>
          Progress Overview
        </Typography>
        <Grid container spacing={2}>
        <Grid item sx={{ width: { xs: '100%', md: '23%' } }}>
            <ProgressCard
              title="Subject Planning"
              icon={MenuBookIcon}
              progressData={dashboardData.subjectPlanning}
              progressColor={THEME_COLORS.primary}
              iconBg="rgba(39, 35, 97, 0.1)"
            />
          </Grid>
          <Grid item sx={{ width: { xs: '100%', md: '23%' } }}>
            <ProgressCard
              title="Assessments"
              icon={AssignmentIcon}
              progressData={dashboardData.assessments}
              progressColor="#f44336"
              iconBg="rgba(244, 67, 54, 0.1)"
            />
          </Grid>
          <Grid item sx={{ width: { xs: '100%', md: '23%' } }}>
            <ProgressCard
              title="Lesson Plans"
              icon={DescriptionIcon}
              progressData={dashboardData.lessonPlans}
              progressColor="#4caf50"
              iconBg="rgba(76, 175, 80, 0.1)"
            />
          </Grid>
          <Grid item sx={{ width: { xs: '100%', md: '23%' } }}>
            <ProgressCard
              title="Moderation"
              icon={SchoolIcon}
              progressData={dashboardData.moderation}
              progressColor="#ff9800"
              iconBg="rgba(255, 152, 0, 0.1)"
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Upcoming Tasks & Recent Activities Section */}
      <Grid item xs={12} width='100%' alignItems="stretch">
        <Grid container spacing={2}>
          {/* Upcoming Tasks */}
          <Grid item sx={{ width: { xs: '100%', md: '48%' } }}>
            <StyledCard elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="medium" mb={2}>
                  Upcoming Tasks
                </Typography>
                <List disablePadding>
                  {dashboardData.upcomingTasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getStatusIcon(task.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          primaryTypographyProps={{
                            fontWeight: task.status === 'urgent' ? 'bold' : 'normal'
                          }}
                          secondary={`Due: ${task.dueDate}`}
                        />
                      </ListItem>
                      {index < dashboardData.upcomingTasks.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Recent Activities */}
          <Grid item sx={{ width: { xs: '100%', md: '48%' } }}>
            <StyledCard elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="medium" mb={2}>
                  Recent Activities
                </Typography>
                <List disablePadding>
                  {dashboardData.recentActivities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getActivityIcon(activity.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.title}
                          secondary={`Date: ${activity.date}`}
                        />
                      </ListItem>
                      {index < dashboardData.recentActivities.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Dashboard;
